import { toast } from '@/features/toast/toast'
import { useQueryParams } from '@/utils/url/use-query-params'
import { useClerk, useUser } from '@clerk/nextjs'
import { useSignIn } from '@clerk/nextjs/legacy'
import { useRouter } from 'next/navigation'
import { AUTH_STEP_QUERY_PARAMS, IAuthStepQueryParams } from '../helpers/auth-constants'
import { useEffect, useRef, useState } from 'react'

/** Tickets are single-use; marks the one this tab already consumed so a remount never retries it. */
const TICKET_CONSUMED_STORAGE_KEY = 'clerk-ticket-consumed'

export const useCheckForSignInToken = (afterSuccessfulUrl: string) => {
    const router = useRouter()
    const { signIn, setActive } = useSignIn()
    const { signOut } = useClerk()
    const { user } = useUser()
    const { getParam } = useQueryParams<IAuthStepQueryParams>()
    // `__clerk_ticket` is the canonical Clerk ticket param (native dashboard impersonation, handed
    // here by the middleware). `token` is our legacy internal-tool param. Accept either.
    const signInToken = getParam(AUTH_STEP_QUERY_PARAMS.ClerkTicket) ?? getParam(AUTH_STEP_QUERY_PARAMS.Token)
    // Loading from the first paint whenever a ticket is present, so the operator sees a signing-in
    // state instead of the sign-in form while ClerkJS boots and the ticket is consumed.
    const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(signInToken))

    const checkedForSignInToken = useRef<boolean>(false)

    useEffect(() => {
        if (!signIn || !setActive || !signInToken || checkedForSignInToken.current) {
            return
        }

        // A remount while the flow settles (back navigation, HMR) must not retry the single-use
        // ticket; once the session from the original attempt lands, continue into the app.
        if (sessionStorage.getItem(TICKET_CONSUMED_STORAGE_KEY) === signInToken) {
            if (user) {
                router.replace(afterSuccessfulUrl)
            } else {
                setIsLoading(false)
            }
            return
        }

        const createSignIn = async () => {
            checkedForSignInToken.current = true
            sessionStorage.setItem(TICKET_CONSUMED_STORAGE_KEY, signInToken as string)
            setIsLoading(true)
            try {
                // Impersonation replaces the active session. Because the middleware intercepts the
                // ticket-accept, Clerk's server-side sign-out step is skipped; a signed-in operator
                // would otherwise make signIn.create({ ticket }) silently no-op. Sign out first.
                // The callback form signs out WITHOUT navigating: a redirect here reloads the page
                // mid-flow, and the remounted page would retry the already-consumed ticket.
                if (user) {
                    await signOut(() => {})
                }

                const signInAttempt = await signIn.create({
                    strategy: 'ticket',
                    ticket: signInToken as string,
                })

                if (signInAttempt.status === 'complete') {
                    await setActive({ session: signInAttempt.createdSessionId })
                    router.replace(afterSuccessfulUrl)
                } else {
                    console.error(JSON.stringify(signInAttempt, null, 2))
                    toast.error('Impersonation could not be completed. The link may be expired or already used.')
                    setIsLoading(false)
                }
            } catch (err) {
                console.error('Error:', JSON.stringify(err, null, 2))
                toast.error('Impersonation failed. Generate a fresh link and try again.')
                setIsLoading(false)
            }
        }

        createSignIn()
    }, [signIn, setActive, signInToken, user, signOut, router, afterSuccessfulUrl])

    return {
        isLoading,
    }
}
